import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { withSize } from 'react-sizeme';
import { makeStyles } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import clsx from 'clsx';
import { Colors, Metrics } from '../../themes';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Select from '../common/Select';
import { languages, rolesArray } from '../../config/AppConfig';
import Button from '@material-ui/core/Button';
import SvgIcon from '../../icons/SvgIcon';
import Table from '../common/Table';
import DetailsTableHeader from '../common/DetailsTableHeader';
import { validateField, checkRole } from '../../lib/Utils';
import SendIcon from '@material-ui/icons/Send';
import Switch from '@material-ui/core/Switch';
import FormControlLabel from '@material-ui/core/FormControlLabel';

const useStyles = makeStyles((theme) => {
  return {
    DetailsWidget: {
      display: 'flex',
      flexDirection: 'column',
      overflowWrap: 'break-word',
    },
    headerButtonsContainer: {
      background: Colors.navBackgroundGrey,
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      borderRadius: '4px 4px 0 0',
      padding: 10,
      [theme.breakpoints.down(Metrics.minWidth)]: {
        padding: 6,
        justifyContent: 'flex-start',
      },
    },
    button: {
      margin: theme.spacing(2),
      fontSize: 14,
      color: Colors.white,
      border: `1px solid ${Colors.disabledGrey}`,
      '&:hover': {
        background: Colors.disabledGrey,
      },
      '&:disabled': {
        border: `1px solid ${Colors.disabledGrey}`,
        background: Colors.navBackgroundGrey,
        '&:hover': {
          background: Colors.black,
        },
      },
    },
    smallSizeButton: {
      height: 35,
      fontSize: 12,
      paddingLeft: 6,
      paddingRight: 6,
    },
    saveButton: {
      background: Colors.blue,
      border: 'none',
      '&:hover': {
        background: Colors.lightBlue,
      },
    },
    deleteButton: {
      margin: 10,
      marginRight: 'auto',
      minWidth: 38,
      width: 38,
      maxWidth: 38,
      padding: 0,
      borderColor: Colors.red,
      '&:hover': {
        background: Colors.red,
      },
      [theme.breakpoints.down(Metrics.minWidth)]: {
        marginRight: 8,
      },
      '&:disabled:hover': {
        background: Colors.black,
      },
    },
    // user details
    detailsContainer: {
      padding: '25px 20px',
      [theme.breakpoints.down('xs')]: {
        padding: 15,
      },
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    formContainer: {
      alignSelf: 'center',
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
    },
    tfaSwitch: {
      marginTop: 40,
      marginLeft: 0,
      color: Colors.lightGrey,
    },
    resetPasswordButton: {
      marginTop: 20,
      marginLeft: 0,
    },
    header2: {
      marginTop: 20,
    },
    group2: {
      paddingBottom: 10,
    },
  };
});

const UserDetails = (props) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { width } = props.size;
  const isSmall = width < Metrics.formDetails.small;
  const formInputWidth = isSmall ? width - 40 : (width - 60) / 2;
  const {
    deleteButtonText,
    add1ButtonText,
    onDeleteButtonClick,
    onAdd1ButtonClick,
    onSaveButtonClick,
    isDeleteButtonDisabled,
    isSaveButtonDisabled,
    hideDetails,
    onKeyPressFormSubmit,
    noDetailsTable,
    onChange,
    onChangeTfa,
    username,
    phone,
    tfa,
    isUntouched,
    setUntouched,
  } = props;
  const smallerThanMedium = useMediaQuery(`(max-width: ${Metrics.breakpoints.md}px)`);
  const role = useSelector((state) => state.auth.role);
  const isSystemAdmin = checkRole(role, 'systemAdmin');
  const isDistributorAdmin = checkRole(role, 'distributorAdmin');
  const translatedRoles = rolesArray
    .filter(({ name }) => {
      if (isSystemAdmin) {
        return true;
      }
      if (isDistributorAdmin) {
        return name !== 'systemAdmin';
      }
      return name !== 'systemAdmin' && name !== 'distributorAdmin';
    })
    .map((role) => ({ ...role, name: t(`roles.${role.name}`) }));
  const [isUsernameError, setUsernameError] = useState(false);
  const [usernameHelperText, setUsernameHelperText] = useState(null);
  const [isUsernameTouched, setUsernameTouched] = useState(false);
  const [isPhoneError, setPhoneError] = useState(false);
  const [phoneHelperText, setPhoneHelperText] = useState(null);
  const [isFormValid, setFormValid] = useState(false);
  useEffect(() => {
    const validate = () => {
      const isUsernameValidEmail = validateField('email', username);
      const isUsernameValid = username && isUsernameValidEmail;
      const isPhoneValid = !tfa || validateField('phone', phone);
      if ((!isUsernameValid && !isUsernameTouched) || !isPhoneValid) {
        setFormValid(false);
      }
      if (username && !isUsernameValidEmail && isUsernameTouched) {
        setUsernameError(true);
        setUsernameHelperText(t('validation.validEmail'));
        setFormValid(false);
      }
      if (!username && isUsernameTouched) {
        setUsernameError(true);
        setUsernameHelperText(t('validation.requiredField'));
        setFormValid(false);
      }
      if (isUsernameValid) {
        setUsernameError(false);
        setUsernameHelperText(null);
      }
      if (!isPhoneValid) {
        setPhoneError(true);
        setPhoneHelperText(t(!phone ? 'validation.requiredField' : 'validation.phoneFormat'));
        setFormValid(false);
        setUsernameTouched(true);
        setUntouched(false);
      } else if (isPhoneValid) {
        setPhoneError(false);
        setPhoneHelperText(null);
      }
      if (isUsernameValidEmail && isPhoneValid) {
        setFormValid(true);
      }
    };
    validate();
  }, [username, isUsernameTouched, phone, tfa, t]);

  const setFieldsTouched = useCallback(
    (value) => {
      setUsernameTouched(value);
      setUntouched(false);
    },
    [setUntouched]
  );

  useEffect(() => {
    if (isUntouched) {
      setFieldsTouched(false);
      setUsernameError(false);
      setUsernameHelperText(null);
    }
  }, [isUntouched, setFieldsTouched]);

  const handleSubmit = (event) => {
    event.preventDefault();
    onSaveButtonClick();
  };

  const handleBlur = (event) => {
    const { name } = event.target;
    if (name === 'username') {
      setUsernameTouched(true);
      setUntouched(false);
    }
  };

  return (
    <div className={classes.DetailsWidget}>
      <form
        id="detailsForm"
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit}
        onKeyPress={(event) => onKeyPressFormSubmit(event)}
      >
        <div className={classes.headerButtonsContainer}>
          <Button
            type="button"
            className={clsx(classes.deleteButton, isSmall && classes.smallSizeButton)}
            variant="outlined"
            aria-label={deleteButtonText}
            onClick={onDeleteButtonClick}
            disabled={isDeleteButtonDisabled}
          >
            <SvgIcon
              icon={'Trash'}
              color={isDeleteButtonDisabled ? Colors.disabledGrey : Colors.white}
              stroke={2}
              width={14}
            />
          </Button>
          <Button
            type="button"
            variant="outlined"
            className={clsx(classes.button, isSmall && classes.smallSizeButton)}
            onClick={onAdd1ButtonClick}
            endIcon={<SvgIcon icon="Add" width={smallerThanMedium ? 16 : 20} color={Colors.white} />}
          >
            {add1ButtonText}{' '}
          </Button>
          <Button
            type="submit"
            variant="contained"
            className={clsx(classes.button, classes.saveButton, isSmall && classes.smallSizeButton)}
            endIcon={
              <SvgIcon
                icon="Save"
                width={smallerThanMedium ? 16 : 20}
                color={!isFormValid ? Colors.disabledGrey : Colors.white}
              />
            }
            disabled={!isFormValid}
            onMouseEnter={() => setFieldsTouched(true)}
          >
            {t('Global_save')}
          </Button>
        </div>
        {!hideDetails && (
          <div className="bodyComponent">
            <div className={classes.detailsContainer}>
              <Typography variant="h2">{t('accessLevelManager_aLevelInfo_header').toUpperCase()}</Typography>
              <div className={classes.formContainer}>
                <TextField
                  style={{ width: formInputWidth }}
                  id="username"
                  type="email"
                  autoComplete="off"
                  error={isUsernameError}
                  helperText={usernameHelperText}
                  label={t('userManager_userInfo_userName')}
                  name="username"
                  margin="normal"
                  value={username}
                  onChange={onChange}
                  onBlur={handleBlur}
                  required
                />
                <TextField
                  style={{ width: formInputWidth }}
                  id="firstName"
                  label={t('userManager_userInfo_firstName')}
                  name="firstName"
                  margin="normal"
                  value={props.firstName}
                  onChange={onChange}
                />
                <TextField
                  style={{ width: formInputWidth }}
                  id="lastName"
                  label={t('userManager_userInfo_lastName')}
                  name="lastName"
                  margin="normal"
                  value={props.lastName}
                  onChange={onChange}
                />
                <Select
                  className={classes.select}
                  selectWidth={formInputWidth}
                  selectData={languages}
                  selectLabel={t('userManager_userInfo_language')}
                  selectValue={props.languageId}
                  onSelect={props.onSelectLanguage}
                  accessValue={'id'}
                  accessLabel={'name'}
                />

                <TextField
                  style={{ width: formInputWidth }}
                  id="phone"
                  name="phone"
                  value={phone}
                  onChange={onChange}
                  margin="normal"
                  label={t('personManager_personInfo_mobile')}
                  error={isPhoneError}
                  helperText={phoneHelperText}
                  required={tfa}
                />
                <FormControlLabel
                  className={classes.tfaSwitch}
                  style={{ width: formInputWidth }}
                  control={<Switch checked={tfa} onChange={(event) => onChangeTfa(event)} value="tfa" />}
                  label={t('auth.twoFactorAuthentication')}
                  labelPlacement="start"
                />

                {!props.isNewUser && (
                  <Button
                    type="button"
                    variant="outlined"
                    className={clsx(classes.button, isSmall && classes.smallSizeButton, classes.resetPasswordButton)}
                    onClick={props.resetPasswordButtonClick}
                    endIcon={<SendIcon />}
                    disabled={isSaveButtonDisabled}
                  >
                    {props.resetPasswordButtonText}{' '}
                  </Button>
                )}
              </div>
              <Typography className={classes.header2} variant="h2">
                {t('userManager_permissions_header').toUpperCase()}
              </Typography>
              <div className={classes.group2}>
                <Select
                  className={classes.select}
                  selectWidth={formInputWidth}
                  selectData={translatedRoles}
                  selectLabel={t('userManager_permissions_userGroup')}
                  selectValue={props.userGroupId}
                  onSelect={props.onSelectUserGroup}
                  accessValue={'id'}
                  accessLabel={'name'}
                />
              </div>
            </div>
          </div>
        )}
      </form>
      {!hideDetails && !noDetailsTable && (
        <>
          <DetailsTableHeader {...props} />
          <Table {...props} />
        </>
      )}
    </div>
  );
};

export default withSize()(UserDetails);
